import SVTAPI from './svtapi';

class Item extends SVTAPI {
  /**
   * Initializes a new, existing item.
   *
   * @param {number} pid The pid of the item
   */
  constructor(pid) {
    super('/item');
    this._pid = pid;
  }

  /**
   * GET /item/:pid
   * Gets the data of a single item.
   *
   * @returns {Object} The server's response
   */
  get() {
    return super.get({ url: '/' + this._pid });
  }

  /**
   * PATCH /item/:pid
   * Updates an existing item.
   *
   * @param {Object}  data  - The data to update
   * @param {boolean} async - Asynchronous or synchronous request. Default is true.
   *
   * @returns {Object} - The server's response
   */
  patch(data, async = true) {
    return super.patch({ url: '/' + this._pid, data, async });
  }

  /**
   * POST /item/:pid/atj
   * Fetches Autovista data.
   *
   * @returns {Object} The server's response
   */
  atj() {
    return super.post({ url: '/' + this._pid + '/atj' });
  }

  /**
   * POST /item/:pid/trafi
   * Fetches Traficom data.
   *
   * @returns {Object} The server's response
   */
  trafiVehicle() {
    return super.post({ url: '/' + this._pid + '/trafi_vehicle' });
  }

  /**
   * POST /item/:pid/trafi_archive
   * Archives existing traficom data for vehicle.
   *
   * @returns {Object} The server's response
   */
  trafiArchive() {
    return super.post({ url: '/' + this._pid + '/trafi_archive' });
  }

  /**
   * DELETE /item/:pid
   * Marks an item as `deleted`
   *
   * @returns {Object} The server's response
   */
  delete() {
    return super.remove({ url: '/' + this._pid });
  }

  /**
   * POST /item/:pid/archive
   * Archives an item
   *
   * @returns {Object} The server's response
   */
  archive() {
    return super.post({ url: '/' + this._pid + '/archive' });
  }

  /**
   * POST /item/:pid/unarchive
   * Un-archives an item
   *
   * @returns {Object} The server's response
   */
  unarchive() {
    return super.post({ url: '/' + this._pid + '/unarchive' });
  }

  /**
   * GET /item/:pid/history
   * Gets history data of an item.
   *
   * @returns {Object} The server's response
   */
  getHistory() {
    return super.get({ url: '/' + this._pid + '/history' });
  }

  /**
   * GET /item/:pid/expenses
   * Gets item's expenses.
   *
   * @returns {Object} The server's response
   */
  getExpenses() {
    return super.get({ url: '/' + this._pid + '/expenses' });
  }

  /**
   * POST /item/:pid/expenses
   * Creates a new expense for the item.
   *
   * @param   {Object} data The expense data
   * @returns {Object}      The server's response
   */
  createExpense(data) {
    return super.post({ url: '/' + this._pid + '/expenses', data });
  }

  /**
   * GET /item/:pid/comments
   * Gets item's comments.
   *
   * @returns {Object} The server's response
   */
  getComments() {
    return super.get({ url: '/' + this._pid + '/comments' });
  }

  /**
   * POST /item/:pid/comments
   * Creates a new comment for the item.
   *
   * @param   {string} comment  The comment data
   * @param   {boolean} important - Whether the comment should also be marked important
   * @returns {Object}          The server's response
   */
  createComment(comment, important = false) {
    const commentData = {
      comment
    };
    if(important) {
      commentData.important = '1';
    }
    return super.post({ url: '/' + this._pid + '/comments', data: commentData });
  }

  /**
   * POST /item/:pid/hide_field
   * Hides a certain field.
   *
   * @param   {string} field  The name of the field
   * @returns {Object}        The server's response
   */
  hideField(field) {
    return super.post({ url: '/' + this._pid + '/hide_field', data: { field } });
  }

  /**
   * POST /item/:pid/show_field
   * Shows a certain field.
   *
   * @param   {string} field  The name of the field
   * @returns {Object}        The server's response
   */
  showField(field) {
    return super.post({ url: '/' + this._pid + '/show_field', data: { field } });
  }

  /**
   * GET /item/:pid/status
   * Gets the status of an item.
   *
   * @returns {Object} The server's response
   */
  getStatus() {
    return super.get({ url: '/' + this._pid + '/status' });
  }

  /**
   * POST /item/:pid/lock
   * Locks an item for current user, so other users cannot edit this item.
   *
   * @returns {Object} The server's response
   */
  lock() {
    return super.post({ url: '/' + this._pid + '/lock' });
  }

  /**
   * POST /item/:pid/unlock
   * Unlocks an item.
   *
   * @returns {Object} The server's response
   */
  unlock() {
    return super.post({ url: '/' + this._pid + '/unlock' });
  }

  /**
   * POST /item/:pid/status
   * Updates the status of an item.
   *
   * @param   {string} stage  Stage where the step belongs
   * @param   {string} step   Step where the status changes
   * @param   {string} status The new status
   * @returns {Object}        The server's response
   */
  updateStatus(stage, step, status) {
    return super.post({ url: '/' + this._pid + '/status', data: { stage, step, status } });
  }

  /**
   * POST /item/:pid/send_to_webshop
   * Sends the item to webshop.
   *
   * @returns {Object} The server's response
   */
  sendToWebshop() {
    return super.post({ url: '/' + this._pid + '/send_to_webshop' });
  }

  /**
   * POST /item/:pid/remove_from_webshop
   * Removes the item from webshop.
   *
   * @returns {Object} The server's response
   */
  removeFromWebshop() {
    return super.post({ url: '/' + this._pid + '/remove_from_webshop' });
  }

  /**
   * POST /item/:pid/weights
   *
   * @param {array} weights - Array of objects containing the images' pids and weights, e.g.:
   *   [
   *     { pid: 73, weight: 100000 },
   *     { pid: 79, weight: 200000 },
   *     ...
   *   ]
   * @returns {Object} The server's response
   */
  updateWeights(weights) {
    return super.post({ url: '/' + this._pid + '/weights', data: { weights } });
  }

  /**
   * POST /item/:pid/location
   *
   * @param {Object} data - The new location data, e.g.:
   * {
   *   warehouse: 13,
   *   address_lat:   37.23,
   *   address_lon: -115.80,
   *   address_street: 'Groom Rd.'
   *   address_zip: '89044',
   *   address_city: 'Nevada',
   *   address_country: 'U.S.',
   *   address_details: 'No spaceship parking during weekends 4pm - 10pm',
   * }
   * @returns {Object} The server's response
   */
  updateLocation(data) {
    return super.post({ url: '/' + this._pid + '/location', data });
  }

  updateLocationSjoberg(data) {
    return super.post({ url: '/' + this._pid + '/location_sjoberg', data });
  }

  /**
   * POST /item/:pid/cancel_sale
   * Cancels item sale.
   *
   * @param {Object} data - Cancel data, e.g.
   * {
   *   reason: 'Item was not as described'
   * }
   *
   * @returns {Object} - The server's response
   */
  cancelSale(data) {
    return super.post({ url: '/' + this._pid + '/cancel_sale', data });
  }

  /**
   * POST /item/:pid/return_to_warehouse
   *
   * @param {Object}        data           - Warehouse data
   * @param {String|Number} data.warehouse - Warehouse pid
   *
   * @returns {Object} - The server's response
   */
  /*returnToWarehouse(data) {
    return super.post({url: '/' + this._pid + '/return_to_warehouse', data });
  }*/

  /**
   * Updates item's order's data
   * PATCH /item/:pid/order
   *
   * @param {Object} data - Order data to update
   *
   * @returns {Object} - The server's response
   */
  updateOrder(data) {
    return super.patch({url: '/' + this._pid + '/order', data });
  }

  /**
   * Changes master item client
   *
   * @param {object} data - Data to update. Expects {client: 8}
   *
   * @returns {Object} - The server's response
   */
  changeClient(data) {
    return super.patch({url: '/' + this._pid + '/client', data});
  }

  /**
   * Places an order for more images for given item/
   *
   * @param {Object} data - Image order data. Expects { description }
   * @returns {Object} - The server's response
   */
  orderImages(data) {
    return super.post({ url: '/' + this._pid + '/order-images', data });
  }

  /**
   * Combines existing item with "ghost" item.
   * Ghost item is an item that is associated with a pre storage event.
   * PATCH /item/:pid/combine/:ghostpid
   *
   * @param {number} ghostpid - ghost item pid
   *
   * @returns {Object} - The server's response
   */
  combine(ghostpid) {
    return super.patch({ url: '/' + this._pid + '/combine/' + ghostpid});
  }

  /**
   * Sets image visibility state for delivery orders.
   * @param {Object} hideShowData -
   * @param {Array} hideShowData.hide - list of image PIDs to hide from delivery
   * @param {Array} hideShowData.show - list of image PIDs to show in delivery
   * @returns {Object} - The server's response
   */
  setDeliveryImages(hideShowData) {
    return super.post({ url: '/' + this._pid + '/setDeliveryImages', data: hideShowData });
  }
}

export default Item;
